import React from "react"
import PropTypes from "prop-types"
import moment from "moment"
import { useTranslation } from "react-i18next"
import { Flex } from "antd"
import { AVAILABILITY_STATUSES, REASON_TYPES } from "../constants"
import { formatTicketedSlot, getBookableSlots, isTicketedListing, summariseSlots } from "../utils"
import ReasonChip from "./ReasonChip"
import styles from "./AvailabilityDayCard.module.css"

const STATUS_CLASS = {
  [AVAILABILITY_STATUSES.AVAILABLE]: styles.available,
  [AVAILABILITY_STATUSES.LIMITED]: styles.limited,
  [AVAILABILITY_STATUSES.FULLY_BOOKED]: styles.fullyBooked,
  [AVAILABILITY_STATUSES.NOT_AVAILABLE]: styles.notAvailable
}

/**
 * One day in the availability grid.
 *
 * The card is always the same height; only the coloured "state" block inside it changes. A day the
 * feed does not describe renders as an empty card rather than guessing a status — the server owns
 * that call (A2), and the endpoint is not built yet, so "unknown" is the common case today.
 *
 * Hovering swaps the collapsed slot line for the reasons in full plus a View Details link, which
 * is why both are rendered and CSS decides: measuring on hover would reflow the whole grid.
 */
const AvailabilityDayCard = ({ dateKey, isCurrentMonth, day, listing, onOpenDetails }) => {
  const { t } = useTranslation()

  const dayNumber = moment(dateKey).format("D")
  const reasons = day?.reasons || []
  const isTicketed = isTicketedListing(listing)
  // A private day drops its unbookable slots, so everything below counts the same array rather
  // than slicing `day.slots` by a length derived from a different one.
  const slots = getBookableSlots(day, isTicketed)
  const { times, remainingCount } = summariseSlots(slots)
  const hasSlots = slots.length > 0

  const slotLine = isTicketed ? slots.slice(0, times.length).map((slot) => formatTicketedSlot(slot, t)) : [times.join(", ")]

  return (
    <div className={`${styles.card} ${isCurrentMonth ? "" : styles.outsideMonth}`}>
      <span className={styles.dayNumber}>{dayNumber}</span>

      {day && (
        <Flex vertical gap="var(--spacing-xxs)" className={`${styles.state} ${STATUS_CLASS[day.status] || ""}`}>
          {reasons.length > 0 && (
            <Flex vertical gap="var(--spacing-xxs)" align="flex-start" className={styles.reasons}>
              {reasons.map((reason, index) => (
                <ReasonChip key={`${reason.type}-${reason.blackout_id || index}`} reason={reason} />
              ))}
            </Flex>
          )}

          {hasSlots && (
            <div className={styles.slots}>
              {slotLine.map((line) => (
                <p key={line} className={styles.slotLine}>
                  {line}
                </p>
              ))}
              {remainingCount > 0 && (
                <p className={styles.slotLine}>
                  {t("availability.more_slots", { defaultValue: "+{{count}} more", count: remainingCount })}
                </p>
              )}
            </div>
          )}

          {/* Detail and the link live in the flow but stay hidden until hover, so the row never reflows. */}
          <div className={styles.hoverDetail}>
            {/* A day with nothing bookable says so, rather than leaving the reader to infer it
                from the absence of a slot line. */}
            {!hasSlots && (
              <p className={styles.detailText}>
                {t("availability.no_start_times", { defaultValue: "No available start times." })}
              </p>
            )}
            {reasons.map((reason) => (
              <React.Fragment key={`${reason.type}-${reason.blackout_id || reason.label}`}>
                {reason.detail && <p className={styles.detailText}>{reason.detail}</p>}
                {reason.items?.length > 0 && (
                  <ul className={styles.detailList}>
                    {reason.items.map((item) => (
                      <li key={item.label}>{item.label}</li>
                    ))}
                  </ul>
                )}
              </React.Fragment>
            ))}
            {/* A `fully_booked` reason already states the count in its own detail line, and the
                design shows it once. */}
            {day.bookings_count > 0 && !reasons.some((reason) => reason.type === REASON_TYPES.FULLY_BOOKED) && (
              <ul className={styles.detailList}>
                <li>{t("availability.bookings_count", { defaultValue: "{{count}} booking", count: day.bookings_count })}</li>
              </ul>
            )}
            <button type="button" className={styles.detailsLink} onClick={() => onOpenDetails(dateKey)}>
              {t("availability.view_details", { defaultValue: "View Details" })}
            </button>
          </div>
        </Flex>
      )}
    </div>
  )
}

AvailabilityDayCard.propTypes = {
  dateKey: PropTypes.string.isRequired,
  isCurrentMonth: PropTypes.bool,
  /** Null when the feed does not describe this day — see the note above. */
  day: PropTypes.shape({
    status: PropTypes.string,
    slots: PropTypes.array,
    slots_total: PropTypes.number,
    bookings_count: PropTypes.number,
    reasons: PropTypes.array
  }),
  listing: PropTypes.shape({ listing_type: PropTypes.string }),
  onOpenDetails: PropTypes.func.isRequired
}

export default AvailabilityDayCard
